/**
 * AI-generated code, moderated by Lukasz Zygmanski
 */
import dayjs from 'dayjs';
import {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';

import {Button} from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {useAuthUser} from '@/hooks/useAuth';
import {useVisibleReleaseNotes} from '@/shared/release-notes/hooks/useVisibleReleaseNotes';
import {releaseBuildMetadata} from '@/shared/release-notes/utils/releaseNotes';
import {
  getLocalizedReleaseNotes,
  getModalReleaseNotes,
  getSeenReleaseNoteIds,
  getUnseenReleaseNotes,
  hasReleaseNotesSeenState,
  initializeReleaseNotesSeenState,
  markVisibleReleaseNotesAsSeen,
} from '@/shared/release-notes/utils/releaseNoteUtils';
import {TIME_FORMATS} from '@/utils/timeFormats';

import ReleaseNoteMarkdown from './ReleaseNoteMarkdown';

function WhatsNewDialog() {
  const {i18n, t} = useTranslation();
  const {data: authUser} = useAuthUser();
  const [open, setOpen] = useState(false);

  const userId = authUser?.userId || authUser?.username;
  const {releaseNotesReady, visibleReleaseNoteIds, visibleReleaseNotes} =
    useVisibleReleaseNotes();

  const unseenModalReleaseNotes = useMemo(() => {
    if (!releaseNotesReady || !userId || !hasReleaseNotesSeenState(userId)) {
      return [];
    }

    const unseenReleaseNotes = getUnseenReleaseNotes(
      getModalReleaseNotes(visibleReleaseNotes),
      getSeenReleaseNoteIds(userId)
    );

    return getLocalizedReleaseNotes(
      unseenReleaseNotes,
      i18n.resolvedLanguage || i18n.language
    );
  }, [
    i18n.language,
    i18n.resolvedLanguage,
    releaseNotesReady,
    userId,
    visibleReleaseNotes,
  ]);

  useEffect(() => {
    if (!releaseNotesReady) return;

    if (!userId) {
      setOpen(false);
      return;
    }

    if (!hasReleaseNotesSeenState(userId)) {
      initializeReleaseNotesSeenState(userId, visibleReleaseNoteIds);
      setOpen(false);
      return;
    }

    setOpen(unseenModalReleaseNotes.length > 0);
  }, [
    releaseNotesReady,
    unseenModalReleaseNotes.length,
    userId,
    visibleReleaseNoteIds,
  ]);

  const handleMarkAsSeen = () => {
    if (userId) {
      markVisibleReleaseNotesAsSeen(userId, visibleReleaseNoteIds);
    }
    setOpen(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setOpen(true);
      return;
    }

    handleMarkAsSeen();
  };

  if (!unseenModalReleaseNotes.length) return null;

  const formattedBuildDate = dayjs(releaseBuildMetadata.buildDate).format(
    TIME_FORMATS.dateTime
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent hasDescription className="md:max-w-3xl">
        <DialogHeader>
          <div>
            <DialogTitle>{t("What's new")}</DialogTitle>
            <DialogDescription>
              {t('Build')} {releaseBuildMetadata.buildNumber} ·{' '}
              {formattedBuildDate}
            </DialogDescription>
          </div>
        </DialogHeader>
        <DialogSection className="max-h-[60vh] overflow-y-auto py-0">
          <div className="flex flex-col gap-6">
            {unseenModalReleaseNotes.map(releaseNote => (
              <article
                className="flex flex-col gap-3 border-b border-gray-200 pb-6 last:border-b-0 last:pb-0"
                key={releaseNote.id}
              >
                <h2 className="font-urbanist text-2xl font-medium text-neutral-dark">
                  {releaseNote.title}
                </h2>
                <p className="text-sm text-gray-700">{releaseNote.summary}</p>
                <ReleaseNoteMarkdown body={releaseNote.body} />
              </article>
            ))}
          </div>
        </DialogSection>
        <DialogFooter>
          <Button
            asChild
            className="w-fit"
            variant="secondary"
            onClick={handleMarkAsSeen}
          >
            <Link to="/releases">{t('View all release notes')}</Link>
          </Button>
          <Button variant="primary" onClick={handleMarkAsSeen}>
            {t('Got it')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default WhatsNewDialog;
